import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Collapse, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MoveToInboxRounded from "@mui/icons-material/MoveToInboxRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import HourglassTopRounded from "@mui/icons-material/HourglassTopRounded";
import PlayCircleOutlineRounded from "@mui/icons-material/PlayCircleOutlineRounded";
import BlockRounded from "@mui/icons-material/BlockRounded";
import SyncRounded from "@mui/icons-material/SyncRounded";
import StorageRounded from "@mui/icons-material/StorageRounded";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { api, getToken } from "../utils/helpers";
import { pilotCall } from "./useStageBoard";
import { stageLabel } from "../utils/stages";
import { timeAgo } from "../components/CicdRunHistory";
import { subscribeCicdEvents } from "../hooks/useCicdEvents";
import { SNAPSHOT_JOBS, activeSnapshotJob, rememberSnapshotJob, snapshotJobUrl } from "./snapshotJobClient";

const STATE_LOOK = {
  queued: { color: "var(--iio-text-2)", Icon: HourglassTopRounded }, running: { color: "var(--iio-info)", Icon: PlayCircleOutlineRounded },
  reconciling: { color: "var(--iio-warning)", Icon: SyncRounded }, done: { color: "var(--iio-success)", Icon: CheckCircleOutlineRounded },
  failed: { color: "var(--iio-critical)", Icon: ErrorOutlineRounded }, cancelled: { color: "var(--iio-text-3)", Icon: BlockRounded },
};
const stateOf = job => (["queued", "running", "reconciling", "done", "failed", "cancelled"].includes(job?.state) ? job.state : "reconciling");

// Status als farbiger Text mit Icon wie in der Laufhistorie, keine Pills.
export function SnapshotJobStatus({ job }) {
  const { t } = useTranslation("freigabe");
  const state = stateOf(job);
  const { color, Icon } = STATE_LOOK[state];
  return <Typography variant="body2" role="status" component="span"
    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color, fontWeight: 600, fontFamily: "inherit" }}>
    <Icon aria-hidden="true" sx={{ fontSize: 18 }}/>
    {t(`snapshotQueue.${state === "queued" && !Number.isInteger(job?.position) ? "waiting" : state}`, { position: job?.position })}
  </Typography>;
}

// Schlüssel einer Karte: Quellpaket, Werks- bzw. Zielpakete und Zielstufe. Zwei Werke desselben Quellpakets sind zwei Karten.
const targetPackagesOf = job => (Array.isArray(job.packages) ? job.packages : []).filter(p => p?.packageId);
const packageKeyOf = job => JSON.stringify([job.packageId || job.packageName || job.id,
  targetPackagesOf(job).map(p => p.packageId).sort(), job.targetStage || job.targetId || ""]);
const ACTIVE_STATES = new Set(["queued", "running", "reconciling"]);
const timeOf = job => Date.parse(job.createdAt || job.updatedAt || "") || 0;
const commitOf = job => {
  const sha = job.result?.commit?.sha || job.result?.commit?.commit?.sha || job.result?.sourceSha;
  return typeof sha === "string" && /^[a-f0-9]{7,40}$/i.test(sha) ? sha : null;
};
const muted = { color: "var(--iio-text-2)", fontFamily: "inherit" };
const buttonSx = { textTransform: "none", fontFamily: "inherit", color: "var(--iio-text-1)", borderColor: "var(--iio-border)",
  "&:hover": { borderColor: "var(--iio-text-3)", backgroundColor: "var(--iio-bg-2)" },
  "&.Mui-focusVisible": { outline: "2px solid var(--iio-signal)", outlineOffset: 2 } };

function CommitLabel({ sha }) {
  const { t } = useTranslation("freigabe");
  const [copied, setCopied] = useState(false);
  const canCopy = typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";
  useEffect(() => { if (!copied) return undefined; const timer = setTimeout(() => setCopied(false), 2000); return () => clearTimeout(timer); }, [copied]);
  const copy = async () => { try { await navigator.clipboard.writeText(sha); setCopied(true); } catch { setCopied(false); } };
  return <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
    <Tooltip title={sha}><Box component="span" sx={{ fontFamily: "var(--iio-font-mono)", fontSize: 12 }}>{t("snapshotQueue.commit", { sha: sha.slice(0, 7) })}</Box></Tooltip>
    {canCopy && <Tooltip title={t(copied ? "snapshotQueue.copied" : "snapshotQueue.copyCommit")}>
      <IconButton size="small" aria-label={t("snapshotQueue.copyCommit")} onClick={copy} sx={{ color: "var(--iio-text-3)", "&.Mui-focusVisible": { outline: "2px solid var(--iio-signal)" } }}>
        <ContentCopyRounded sx={{ fontSize: 14 }}/></IconButton>
    </Tooltip>}
  </Box>;
}

function JobCard({ job, spaceId, selected, inMonitor, cancelling, onCancel, compact = false, sourceStage }) {
  const { t, i18n } = useTranslation(["freigabe", "cicdruns", "gemeinsam"]);
  const { t: tRuns } = useTranslation(["cicdruns", "gemeinsam"]);
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
  const state = stateOf(job);
  const sha = commitOf(job);
  const at = job.createdAt && Number.isFinite(Date.parse(job.createdAt)) ? new Date(job.createdAt) : null;
  const target = job.targetStage ? stageLabel(job.targetStage) : null;
  // Quelle aus dem Auftrag, sonst die erste Stufe des Spaces; ohne Angabe entfällt sie.
  const source = job.sourceStage || sourceStage ? stageLabel(job.sourceStage || sourceStage) : null;
  // Werks- bzw. Zielpakete sichtbar, wenn sie vom Quellpaket abweichen.
  const targets = targetPackagesOf(job).filter(p => p.packageId !== job.packageId).map(p => p.packageName || p.packageId);
  const who = [job.userName, job.topicName].filter(Boolean).join(" · ");
  return <Paper id={`snapshot-job-${job.id}`} tabIndex={-1} variant="outlined" component="article" aria-label={job.packageName || job.packageId}
    title={[job.spaceId, job.userId].filter(Boolean).join(" · ") || undefined}
    sx={{ p: compact ? 1.25 : 2, borderRadius: "8px", backgroundColor: "var(--iio-bg-1)", backgroundImage: "none", color: "var(--iio-text-1)",
      borderColor: selected ? "var(--iio-signal)" : "var(--iio-border)", borderLeftWidth: selected ? 3 : 1, fontFamily: "inherit" }}>
    <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1, md: 2 }} alignItems={{ md: "center" }} justifyContent="space-between">
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Inventory2OutlinedIcon aria-hidden="true" sx={{ color: "var(--iio-text-3)" }}/>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, fontFamily: "inherit", overflowWrap: "anywhere", lineHeight: 1.3 }}>{job.packageName || job.packageId}</Typography>
          {targets.length > 0 && <Typography variant="body2" sx={{ ...muted, overflowWrap: "anywhere" }}>{t("snapshotQueue.targetPackages", { names: targets.join(", ") })}</Typography>}
          <SnapshotJobStatus job={job}/>
        </Box>
      </Stack>
      {!compact && target && <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ...muted, fontSize: 14 }}>
        {source && <><StorageRounded aria-hidden="true" sx={{ fontSize: 18 }}/><span>{t("cicdruns:lauf.quelle")} <strong>{source}</strong></span>
        <ChevronRightRounded aria-hidden="true" sx={{ fontSize: 18 }}/></>}
        <FlagOutlined aria-hidden="true" sx={{ fontSize: 18 }}/><span>{t("cicdruns:lauf.ziel")} <strong>{target}</strong></span>
      </Stack>}
      <Stack alignItems={{ md: "flex-end" }} spacing={0.25} sx={{ ...muted, fontSize: 13 }}>
        {at && <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
          <CalendarTodayOutlined aria-hidden="true" sx={{ fontSize: 16 }}/>
          <time dateTime={at.toISOString()} title={at.toLocaleString(locale)}>{at.toLocaleString(locale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</time>
          <span>· {timeAgo(job.createdAt, tRuns)}</span>
        </Box>}
        {sha && <CommitLabel sha={sha}/>}
      </Stack>
    </Stack>
    {!compact && who && <Typography variant="body2" sx={{ ...muted, mt: 1, overflowWrap: "anywhere" }}>{who}</Typography>}
    {job.error?.message && <Alert severity="error" variant="outlined" sx={{ mt: 1.5, borderRadius: "6px", fontFamily: "inherit", color: "var(--iio-text-1)",
      borderColor: "var(--iio-critical)", backgroundColor: "transparent", "& .MuiAlert-icon": { color: "var(--iio-critical)" }, overflowWrap: "anywhere" }}>{job.error.message}</Alert>}
    {(job.state === "queued" && job.canCancel === true) || !inMonitor || state === "done" ? <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
      {job.state === "queued" && job.canCancel === true && <Button variant="outlined" size="small" sx={buttonSx} disabled={cancelling === job.id} onClick={() => onCancel(job)}>{t("snapshotQueue.cancel")}</Button>}
      {/* Im Monitor selbst führt kein Link zurück in den Monitor. */}
      {!inMonitor && <Button variant="outlined" size="small" sx={buttonSx} component="a" href={snapshotJobUrl(spaceId, job.id)} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewRounded/>}>{t("snapshotQueue.monitor")}</Button>}
      {state === "done" && <Button variant="outlined" size="small" sx={buttonSx} component="a" href={`/approvals?spaceId=${encodeURIComponent(spaceId)}`}>{t("snapshotQueue.approvals")}</Button>}
    </Stack> : null}
  </Paper>;
}

function PackageGroup({ jobs, spaceId, selectedId, inMonitor, cancelling, onCancel, sourceStage }) {
  const { t } = useTranslation("freigabe");
  // Aktive Aufträge bleiben immer sichtbar; eingeklappt werden nur ältere abgeschlossene.
  const [newest] = jobs;
  const shown = jobs.filter(job => job === newest || ACTIVE_STATES.has(job.state));
  const older = jobs.filter(job => !shown.includes(job));
  const [open, setOpen] = useState(() => older.some(job => job.id === selectedId));
  useEffect(() => { if (older.some(job => job.id === selectedId)) setOpen(true); }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps
  const card = (job, compact) => <JobCard key={job.id} job={job} spaceId={spaceId} selected={job.id === selectedId} inMonitor={inMonitor}
    cancelling={cancelling} onCancel={onCancel} compact={compact} sourceStage={sourceStage}/>;
  return <Stack spacing={1}>
    {/* In die Pipeline gelegt und damit in der Laufhistorie: nur kompakt. */}
    {shown.map(job => card(job, job.state === "done" && job.id !== selectedId))}
    {older.length > 0 && <>
      <Button size="small" onClick={() => setOpen(value => !value)} aria-expanded={open} sx={{ ...buttonSx, alignSelf: "flex-start" }}
        startIcon={<ExpandMoreRounded sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms" }}/>}>
        {t("snapshotQueue.older", { count: older.length })}
      </Button>
      <Collapse in={open} unmountOnExit><Stack spacing={1} sx={{ pl: { xs: 0, sm: 3 } }}>{older.map(job => card(job, job.id !== selectedId))}</Stack></Collapse>
    </>}
  </Stack>;
}

/** Die Serverliste stellt Aufträge auch nach einem Seitenwechsel wieder her. */
/**
 * Einlegeaufträge eines Space aus der Serverliste (mit Ereignissen und Minutentakt). Gemeinsam genutzt von der Liste in
 * Wizard und Freigabe und von der Laufhistorie im CI/CD Monitor (DEV183); nur Lesen und Abbrechen.
 */
export function useSnapshotJobs({ spaceId, userId, enabled = true, selectedId, refreshKey, onFinished, observationScope = null, autoRefresh = true, observedJobs, observedJobsUnavailable = false, observedJobsForbidden = false }) {
  const { t } = useTranslation("freigabe");
  const pilot = observationScope !== null;
  let identity = null;
  try { if (pilot) identity = getToken(); } catch { /* Ohne Anmeldung keine alte Ansicht behalten. */ }
  const scope = JSON.stringify([spaceId, pilot ? observationScope : null, identity]);
  const [stateScope, setStateScope] = useState(scope);
  const [state, setState] = useState({ jobs: [], loading: false, error: null });
  const [cancelling, setCancelling] = useState(null);
  const generation = useRef(0), readRef = useRef(null), finished = useRef(new Set());
  const observedDenied = useRef(false), observedVersion = useRef(0);
  const finishRef = useRef(onFinished); finishRef.current = onFinished;
  useEffect(() => {
    const current = ++generation.current; observedDenied.current = false;
    setStateScope(scope);
    setState({ jobs: [], loading: !!spaceId && enabled, error: null });
    finished.current = new Set();
    if (!enabled || !spaceId) { readRef.current = null; return; }
    let busy = false, pending = false, live = true, forbidden = false;
    const inScope = () => {
      if (!live || current !== generation.current) return false;
      try { return !pilot || getToken() === identity; } catch { return false; }
    };
    const request = path => pilot ? pilotCall(path, { timeoutMs: 10000 }) : api(path, { retry: false });
    const deny = () => {
      if (!inScope()) return;
      forbidden = true; pending = false; observedDenied.current = true;
      setState({ jobs: [], loading: false, error: t("stageBranches.errors.STAGE_REQUEST_FORBIDDEN") });
    };
    const read = async () => {
      if (!inScope() || forbidden) return;
      if (busy) { pending = true; return; }
      busy = true;
      do {
        pending = false;
        const observationAtStart = observedVersion.current;
        try {
          const result = await request(`${SNAPSHOT_JOBS}?spaceId=${encodeURIComponent(spaceId)}`);
          if (!Array.isArray(result?.jobs)) throw new Error(t("snapshotQueue.invalid"));
          let jobs = result.jobs;
          if (selectedId && !jobs.some(job => job.id === selectedId)) {
            const detail = await request(`${SNAPSHOT_JOBS}/${encodeURIComponent(selectedId)}?spaceId=${encodeURIComponent(spaceId)}`);
            if (detail?.job) jobs = [detail.job, ...jobs];
          }
          if (inScope() && !forbidden && observationAtStart === observedVersion.current) {
            setState({ jobs, loading: false, error: null });
            let completed = null;
            for (const job of jobs) {
              rememberSnapshotJob(userId, spaceId, job.id);
              if (job.state === "done" && !finished.current.has(job.id)) { finished.current.add(job.id); completed = job; }
            }
            if (completed) finishRef.current?.(completed);
          }
        } catch (error) {
          if (pilot && [401, 403].includes(error.status)) deny();
          else if (inScope() && !forbidden) setState(previous => ({ ...previous, loading: false, error: error.message || t("snapshotQueue.readError") }));
        }
      } while (pending && inScope() && !forbidden);
      busy = false;
    };
    readRef.current = read;
    const close = subscribeCicdEvents(spaceId, autoRefresh ? read : () => {}, state => { if (pilot && state === "forbidden") deny(); }, { localRevision: pilot });
    const timer = autoRefresh ? setInterval(() => { if (document.visibilityState !== "hidden") read(); }, 60000) : null;
    read();
    return () => { live = false; close(); clearInterval(timer); readRef.current = null; };
  }, [spaceId, userId, enabled, selectedId, t, scope, pilot, identity, autoRefresh]);
  useEffect(() => { if (refreshKey) readRef.current?.(); }, [refreshKey]);
  useEffect(() => {
    if (!pilot || !enabled || getToken() !== identity || stateScope !== scope || observedDenied.current) return;
    if (observedJobsUnavailable || observedJobsForbidden || Array.isArray(observedJobs)) observedVersion.current++;
    if (observedJobsForbidden) { observedDenied.current = true; setState({ jobs: [], loading: false, error: t("stageBranches.errors.STAGE_REQUEST_FORBIDDEN") }); }
    else if (observedJobsUnavailable) setState(previous => ({ ...previous, loading: false, error: t("snapshotQueue.readError") }));
    else if (Array.isArray(observedJobs)) setState({ jobs: observedJobs, loading: false, error: null });
  }, [pilot, enabled, identity, stateScope, scope, observedJobs, observedJobsUnavailable, observedJobsForbidden, t]);
  const refresh = useCallback(() => readRef.current?.(), []);
  const cancel = async job => {
    const current = generation.current;
    if (pilot && (stateScope !== scope || !enabled || getToken() !== identity)) return;
    setCancelling(job.id);
    try { await api(`${SNAPSHOT_JOBS}/${encodeURIComponent(job.id)}/cancel`, { method: "POST", body: JSON.stringify({ spaceId }) }); await refresh(); }
    catch (error) { if (!pilot || current === generation.current && getToken() === identity) setState(previous => ({ ...previous, error: error.message })); }
    finally { if (!pilot || current === generation.current) setCancelling(null); }
  };
  return { state: pilot && stateScope !== scope ? { jobs: [], loading: enabled, error: null } : state, refresh, cancel, cancelling };
}

export default function SnapshotJobs({ spaceId, userId, enabled = true, activeOnly = false, refreshKey, selectedId, onFinished, observedJob, inMonitor = false, sourceStage, observationScope = null }) {
  const { t } = useTranslation("freigabe");
  const { state, refresh, cancel, cancelling } = useSnapshotJobs({ spaceId, userId, enabled, selectedId, refreshKey, onFinished, observationScope });
  const focused = useRef(null);
  useEffect(() => {
    const key = `${spaceId}:${selectedId}`;
    if (!selectedId || focused.current === key || !state.jobs.some(job => job.id === selectedId)) return;
    focused.current = key;
    const frame = requestAnimationFrame(() => {
      const element = document.getElementById(`snapshot-job-${selectedId}`);
      element?.focus({ preventScroll: true }); element?.scrollIntoView?.({ block: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [spaceId, selectedId, state.jobs]);
  if (!enabled || !spaceId) return null;
  const currentJobs = observedJob?.id ? [observedJob, ...state.jobs.filter(job => job.id !== observedJob.id)] : state.jobs;
  const jobs = currentJobs.filter(job => !activeOnly || activeSnapshotJob(job) || job.id === selectedId);
  if (!jobs.length && !state.error && !state.loading) return null;
  // Je Paket eine Karte mit dem neuesten Eintrag; ältere Einträge desselben Pakets eingeklappt darunter.
  const groups = [...jobs.reduce((map, job) => map.set(packageKeyOf(job), [...(map.get(packageKeyOf(job)) || []), job]), new Map()).values()]
    .map(list => [...list].sort((a, b) => timeOf(b) - timeOf(a)))
    .sort((a, b) => timeOf(b[0]) - timeOf(a[0]));
  return <Stack component="section" spacing={1.5} aria-label={t("snapshotQueue.title")} sx={{ my: 2, minWidth: 0 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <MoveToInboxRounded aria-hidden="true" sx={{ color: "var(--iio-text-2)" }}/>
        <Typography component="h2" variant="h6" sx={{ fontFamily: "inherit", fontWeight: 600, color: "var(--iio-text-1)" }}>{t("snapshotQueue.title")}</Typography>
      </Stack>
      <Button variant="outlined" size="small" sx={buttonSx} onClick={refresh}>{t("snapshotQueue.refresh")}</Button>
    </Stack>
    {observationScope !== null && jobs.length > 0 && <Typography variant="body2" sx={muted}>{t("stageBranches.readModel.accepted")}</Typography>}
    {state.loading && <CircularProgress size={20} aria-label={t("snapshotQueue.loading")} sx={{ color: "var(--iio-signal)" }}/>}
    {state.error && <Alert severity="error" variant="outlined" sx={{ borderRadius: "6px", color: "var(--iio-text-1)", borderColor: "var(--iio-critical)",
      backgroundColor: "transparent", "& .MuiAlert-icon": { color: "var(--iio-critical)" } }}>{t("snapshotQueue.readError")} {state.error}</Alert>}
    {groups.map(list => <PackageGroup key={packageKeyOf(list[0])} jobs={list} spaceId={spaceId} selectedId={selectedId} inMonitor={inMonitor} sourceStage={sourceStage}
      cancelling={cancelling} onCancel={cancel}/>)}
  </Stack>;
}
